/*
 * Copyright © 2018-2023, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useLayoutEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { strings } from "../../../common/Strings/Strings";
import AlertBox from "../../../components/AlertBox";
import { getGeneralError } from "../../../util/helperFunctions";
import Button from "../../../components/Button";
import Modal from "../../../components/Modal/Modal";
import Field from "../../../components/ReactHookFormFields/General/Field";
import moment from "moment/moment";
import LoaderInline from "../../../components/LoaderInline";
import { ClinicChargeRequest } from "../../../models/management/ClinicChargeRequest";
import { ClinicChargeResponse } from "../../../models/charge/ClinicChargeResponse";
import ChargeManagementApi from "../../../api/ChargeManagementApi";
import CloseButton from "../../../components/CloseButton";
import CostSummarySection from "../../Clinic/Settings/Payment/CostSummarySection";
import { AddressRequest } from "../../../models/contact/AddressRequest";
import { AutoCompleteOptions } from "../../../models/AutoCompleteOptions";
import { ClinicDetailsResponse } from "../../../models/management/ClinicDetailsResponse";
import { ContactType } from "../../../models/contact/ContactType";
import CheckBox from "../../../components/ReactHookFormFields/General/CheckBox";

interface Props {
  clinic: ClinicDetailsResponse;
  onHide: () => void;
  show: boolean;
}

interface ClinicChargeForm extends Pick<AddressRequest, "city" | "countryCode" | "street" | "houseDetails" | "zip"> {
  startDate: string;
  endDate: string;
  customBillingAddress: boolean;
}

const StartClinicChargeModal: React.FC<Props> = (props: Props) => {
  const { clinic, onHide, show } = props;
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isPreviewLoading, setPreviewLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [charge, setCharge] = useState<ClinicChargeResponse>();

  const { watch, reset, register, handleSubmit } = useForm<ClinicChargeForm>({
    mode: "onChange",
    defaultValues: {
      startDate: moment().subtract(1, "months").startOf("month").format("YYYY-MM-DD"),
      endDate: moment().subtract(1, "months").endOf("month").format("YYYY-MM-DD"),
      city: clinic.addresses.find((i) => i.contactType == ContactType.PRIMARY)?.city,
      countryCode: clinic.addresses.find((i) => i.contactType == ContactType.PRIMARY)?.countryCode,
      houseDetails: clinic.addresses.find((i) => i.contactType == ContactType.PRIMARY)?.houseDetails,
      street: clinic.addresses.find((i) => i.contactType == ContactType.PRIMARY)?.street,
      zip: clinic.addresses.find((i) => i.contactType == ContactType.PRIMARY)?.zip,
      customBillingAddress: false,
    },
  });

  useLayoutEffect(() => {
    reset();
    setError(null);
  }, [reset, clinic.id, show]);

  const [startDate, endDate, customBillingAddress] = watch(["startDate", "endDate", "customBillingAddress"]);

  const getPreview = async () => {
    try {
      setPreviewLoading(true);
      const request: ClinicChargeRequest = {
        clinicId: clinic.id,
        startDate,
        endDate,
      };
      const response = await ChargeManagementApi.createClinicChargePreview(request);
      setCharge(response.data);
      setError(null);
    } catch (e) {
      setError(await getGeneralError(e));
    } finally {
      setPreviewLoading(false);
    }
  };

  const chargeClinic = async (formData: ClinicChargeForm) => {
    setLoading(true);

    const billingAddress: AddressRequest = {
      city: formData.city,
      countryCode: formData.countryCode,
      houseDetails: formData.houseDetails,
      street: formData.street,
      zip: formData.zip,
    };

    const request: ClinicChargeRequest = {
      clinicId: clinic.id,
      startDate: formData.startDate,
      endDate: formData.endDate,
      billingAddress: formData.customBillingAddress ? billingAddress : undefined,
    };

    try {
      await ChargeManagementApi.createClinicCharge(request);

      onHide();
      setError(null);
    } catch (e) {
      setError(await getGeneralError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void getPreview();
  }, [clinic.id, startDate, endDate]);

  return (
    <>
      <Modal size="modal-lg" show={show} handleClose={onHide}>
        <Modal.Header closeButton title={strings.chargeClinic}></Modal.Header>
        <Modal.Body>
          <form>
            <div className="row mb-5 space-y-6">
              <div className="col">
                <Field register={register} type="date" label={strings.startDate} name="startDate" />
              </div>
              <div className="col">
                <Field register={register} type="date" label={strings.endDate} name="endDate" />
              </div>
            </div>
            <div hidden={!isPreviewLoading}>
              <LoaderInline />
            </div>
            <div hidden={isPreviewLoading}>
              <CostSummarySection hideForecast={true} costs={charge} />
            </div>
            <CheckBox
              className="mt-5"
              name="customBillingAddress"
              register={register}
              label={strings.customBillingAddress}
            />
            {customBillingAddress && (
              <div className="row my-5 space-y-6">
                <Field
                  name="street"
                  register={register}
                  autoComplete={AutoCompleteOptions.off}
                  label={strings.addressLine1}
                  required
                  showRequired
                />
                <Field
                  name="houseDetails"
                  register={register}
                  autoComplete={AutoCompleteOptions.off}
                  label={strings.addressLine2}
                  optional
                />
                <Field
                  name="city"
                  register={register}
                  autoComplete={AutoCompleteOptions.off}
                  label={strings.city}
                  required
                  showRequired
                />
                <Field
                  name="zip"
                  register={register}
                  autoComplete={AutoCompleteOptions.off}
                  label={strings.postalCode}
                  required
                  showRequired
                />
                <Field
                  name="countryCode"
                  register={register}
                  autoComplete={AutoCompleteOptions.off}
                  label={strings.country}
                  required
                  showRequired
                />
              </div>
            )}
            <AlertBox message={error} className="my-3" />
          </form>
        </Modal.Body>

        <Modal.Footer>
          <CloseButton onClick={onHide} />
          <Button
            className="modal-main-button"
            loading={isLoading}
            disabled={isLoading || isPreviewLoading}
            onClick={handleSubmit(chargeClinic)}
          >
            {strings.chargeClinic}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default StartClinicChargeModal;
